import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { z } from 'zod';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import {
  Form,
  FormSection,
  FormField,
  FormActions,
  TextField,
  CountrySelectField,
  PhoneField,
} from 'modules/Common/form/v2';
import { CountryDropdown } from 'modules/Common/utils/Utility';
import { validatePhoneNumber } from 'modules/Common/config/ConsultantOperatorInfoFormConfig';
import { useBranding } from 'modules/App/hooks/useBranding';
import { useFetchLoggedInOperator } from 'modules/Operator/hooks/useFetchLoggedInOperator';
import { useCreateOperator } from 'modules/Operator/hooks/useCreateOperator';
import { useUpdateOperator } from 'modules/Operator/hooks/useUpdateOperator';
import { V } from 'helpers/validationMessages';
import { apiErrorMessage } from 'helpers/serverIssues';

const EMPTY_ADDRESS = {
  additionalLine: '',
  city: '',
  country: '',
  POBox: '',
  postcode: '',
  state: '',
  street: '',
  number: '',
};

const schema = z.object({
  operatorName: z.string().min(1, V.required).max(70, V.maxLength(70)),
  country: z.string().min(1, V.required).or(z.number().transform(String)),
  city: z.string().min(1, V.required).max(35, V.maxLength(35)),
  contactName: z.string().min(1, V.required).max(70, V.maxLength(70)),
  contactEmail: z.string().min(1, V.required).email(),
  phoneNumber: z
    .string()
    .min(1, V.required)
    .refine((v) => v && validatePhoneNumber(v), { message: V.invalidPhone }),
  corporateRegistrationNumber: z.string().max(70, V.maxLength(70)).optional().or(z.literal('')),
});

function defaultsFor(operator, organization) {
  const addr = operator?.address || {};
  return {
    operatorName: operator?.name ?? organization?.name ?? '',
    country: addr.country != null ? String(addr.country) : '',
    city: addr.city ?? '',
    contactName: operator?.contactName ?? '',
    contactEmail: operator?.contactEmail ?? '',
    phoneNumber: operator?.phoneNumber ?? '',
    corporateRegistrationNumber: operator?.corporateRegistrationNumber ?? '',
  };
}

export const isOperatorInfoComplete = (operator) => schema.safeParse(defaultsFor(operator)).success;

function payloadFromValues(values, existingOperator, brand) {
  return {
    ...existingOperator,
    name: values.operatorName,
    address: {
      ...EMPTY_ADDRESS,
      ...(existingOperator?.address || {}),
      country: values.country,
      city: values.city,
    },
    contactName: values.contactName,
    contactEmail: values.contactEmail,
    phoneNumber: values.phoneNumber,
    corporateRegistrationNumber: values.corporateRegistrationNumber || null,
    faradaiClient: brand.key === 'faradai',
  };
}

export default function OperatorInfoForm({
  onSubmit,
  title = 'Operator Information',
  description,
}) {
  const history = useHistory();
  const brand = useBranding();
  const { isLoaded, organization } = useOrganization();
  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  const { data: operator, isLoading: isFetching } = useFetchLoggedInOperator();
  const { mutateAsync: createOperator, isPending: isCreating } = useCreateOperator();
  const { mutateAsync: updateOperator, isPending: isUpdating } = useUpdateOperator();

  const existingOperator = !!operator;
  const isSaving = isCreating || isUpdating;
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // After a brand-new organization is created server-side, the Clerk
  // organization list lags by one revalidation. Track when we're waiting
  // and redirect once the membership list is repopulated.
  const [awaitingOrganization, setAwaitingOrganization] = useState(false);
  const [organizationActivated, setOrganizationActivated] = useState(false);

  useEffect(() => {
    if (awaitingOrganization && organizationActivated) {
      history.push('/connections/invites');
    }
  }, [awaitingOrganization, organizationActivated, history]);

  useEffect(() => {
    const setOrg = async () => {
      if (!organization) {
        const userOrgs = userMemberships?.data;
        if (userOrgs?.length > 0) {
          await setActive({ organization: userOrgs[0].organization.id });
          setOrganizationActivated(true);
        }
      }
    };
    setOrg();
  }, [userMemberships, organization, setActive]);

  const defaults = useMemo(() => defaultsFor(operator, organization), [operator, organization]);

  // The <Form> below is keyed so react-hook-form seeds its defaults from the
  // fetched record + Clerk org once they resolve (RHF reads defaultValues only
  // at mount). We latch that key the first time both sources have settled:
  // without the latch, a window-focus revalidation (Clerk re-checking
  // `isLoaded`, or react-query refetching) would change the key, remount the
  // form, and discard whatever the user had typed.
  const formKeyRef = useRef('loading');
  if (formKeyRef.current === 'loading' && isLoaded && !isFetching) {
    formKeyRef.current = operator?._id || 'new';
  }

  const handleSubmit = async (values) => {
    setSubmitError(null);
    setSubmitted(false);
    const payload = payloadFromValues(values, operator, brand);
    try {
      if (existingOperator) {
        await updateOperator(payload);
      } else {
        const data = await createOperator(payload);
        if (data?.newOrganization) {
          await userMemberships.revalidate();
          setAwaitingOrganization(true);
        }
      }
      setSubmitted(true);
      if (onSubmit) onSubmit(true);
    } catch (err) {
      setSubmitError(apiErrorMessage(err) || err?.response?.data?.error || 'Something went wrong');
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-1">
      <h1>
        {title}{' '}
        {isFetching ? (
          <FontAwesomeIcon
            icon={faSpinner}
            className="mr-2 fa-spin"
          />
        ) : null}
      </h1>
      {description ? <p className="m-0 text-sm text-slate-500">{description}</p> : null}

      {submitError ? (
        <div className="mt-2 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-red-700 m-0">An error occurred</p>
            <p className="text-xs text-red-600 m-0 mt-0.5">{submitError}</p>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setSubmitError(null)}
            className="text-red-400 hover:text-red-600 transition-colors text-sm leading-none"
          >
            ✕
          </button>
        </div>
      ) : null}

      <Form
        key={formKeyRef.current}
        schema={schema}
        defaultValues={defaults}
        onSubmit={handleSubmit}
      >
        <FormSection title="General">
          <FormField
            name="operatorName"
            label="Name"
            required
            colSpan={6}
          >
            <TextField />
          </FormField>
          <FormField
            name="corporateRegistrationNumber"
            label="Corporate Registration Number"
            colSpan={6}
          >
            <TextField />
          </FormField>
        </FormSection>

        <FormSection title="Address">
          <FormField
            name="country"
            label="Country"
            required
            colSpan={6}
          >
            <CountrySelectField
              scope={CountryDropdown.ALL}
              valueAs="id"
              placeholder="Please enter a country"
            />
          </FormField>
          <FormField
            name="city"
            label="City"
            required
            colSpan={6}
          >
            <TextField />
          </FormField>
        </FormSection>

        <FormSection title="Contact Information">
          <FormField
            name="contactName"
            label="Name"
            required
            colSpan={6}
          >
            <TextField />
          </FormField>
          <FormField
            name="contactEmail"
            label="Email"
            required
            colSpan={6}
          >
            <TextField type="email" />
          </FormField>
          <FormField
            name="phoneNumber"
            label="Phone"
            required
            colSpan={6}
          >
            <PhoneField />
          </FormField>
        </FormSection>

        <FormActions
          submitLabel={onSubmit ? 'Next' : 'Save'}
          loading={isSaving}
          submitted={submitted}
          disabled={isFetching}
        />
      </Form>
    </div>
  );
}
